import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Search, ShieldCheck, Star } from "lucide-react";
import { toast } from "sonner";

const REVIEWS = [
  {
    id: "testimonial-card-1",
    name: "Dana Whitfield",
    area: "River District",
    quote:
      "Furnace died at 11 PM in a cold snap. Crowson had a tech in my driveway by 11:24. Heat was back before midnight. Unreal.",
    system: "Forge Furnace Install",
  },
  {
    id: "testimonial-card-2",
    name: "Marcus Oyelaran",
    area: "Northgate",
    quote:
      "They load-calculated my whole house instead of just swapping the box. Summer bill dropped $61 a month. These people are engineers.",
    system: "Ice Tech AC + Zoning",
  },
  {
    id: "testimonial-card-3",
    name: "Priya Raman",
    area: "Lakeshore",
    quote:
      "Third quote I got — the only one that explained the math. Install was one day, zero mess, and the mini-splits are silent.",
    system: "Ductless Mini-Split ×3",
  },
];

export default function TestimonialsArea() {
  const [zip, setZip] = useState("");

  const lookup = () => {
    if (!/^\d{5}$/.test(zip)) {
      toast.error("Enter a valid 5-digit zip code");
      return;
    }
    toast.success(`Yes — we cover ${zip}`, {
      description: "3 certified crews stationed nearby. Same-day appointments available.",
    });
  };

  return (
    <section id="reviews" data-testid="testimonials-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-2xl"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.26em] text-forge">04 — Proof</span>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Neighbors who never think about their thermostat anymore.
          </h2>
        </motion.div>

        <div className="grid gap-4 lg:grid-cols-3">
          {REVIEWS.map((r, i) => (
            <motion.blockquote
              key={r.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              data-testid={r.id}
              className="flex flex-col justify-between rounded-2xl border border-white/[0.08] bg-navy-800/70 p-7 transition-colors duration-300 hover:border-white/20 sm:p-8"
            >
              <div>
                <div className="flex gap-1 text-forge">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-4 w-4 fill-forge" />
                  ))}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-slate-300 sm:text-base">“{r.quote}”</p>
              </div>
              <footer className="mt-7 border-t border-white/[0.07] pt-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-sm font-bold text-slate-50">{r.name}</div>
                    <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                      {r.area} · {r.system}
                    </div>
                  </div>
                  <span className="flex items-center gap-1.5 rounded-full border border-ice/25 bg-ice/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-ice">
                    <ShieldCheck className="h-3 w-3" /> Verified
                  </span>
                </div>
              </footer>
            </motion.blockquote>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          id="coverage"
          data-testid="coverage-section"
          className="mt-16 overflow-hidden rounded-3xl border border-white/[0.08] bg-gradient-to-r from-ice/[0.07] via-navy-800 to-forge/[0.08] p-8 sm:p-12"
        >
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-xl">
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ice">
                <MapPin className="h-3.5 w-3.5" /> Coverage Check
              </span>
              <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
                Are we in your zip?
              </h3>
              <p className="mt-3 text-sm text-slate-400 sm:text-base">
                14 crews across the Nashville metro. Punch in your zip and we’ll confirm
                coverage and the next available appointment.
              </p>
            </div>
            <div className="flex w-full max-w-md gap-2.5">
              <input
                data-testid="zip-search-input"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                onKeyDown={(e) => e.key === "Enter" && lookup()}
                placeholder="Enter zip code"
                inputMode="numeric"
                className="w-full rounded-full border border-white/10 bg-navy-950/70 px-5 py-4 text-sm text-slate-100 placeholder:text-slate-500 focus:border-ice/50 focus:outline-none"
              />
              <button
                data-testid="zip-search-submit"
                onClick={lookup}
                className="flex shrink-0 items-center gap-2 rounded-full bg-gradient-to-br from-ice-lo to-ice px-6 py-4 font-display text-xs font-bold uppercase tracking-wider text-navy-950 transition-transform duration-200 hover:scale-105 active:scale-95"
              >
                <Search className="h-4 w-4" />
                Check
              </button>
            </div>
          </div>
          <div data-testid="zip-search-result" className="sr-only" aria-live="polite" />
        </motion.div>
      </div>
    </section>
  );
}
